import React from "react";
import { Box, Drawer, IconButton } from "@mui/material";
import CustomText, { textStyles } from "../../../../components/CustomText";
import profilecross from "../../../../assets/profilecross.svg";
import StatusDropdown from "../../../../components/StatusDropdown";
import document1 from "../../../../assets/document1.svg"; // ✅ pdf icon
import { useTranslation } from "react-i18next";
import CustomButton from "../../../../components/CustomButton";
import { convertUTCToLocalDateTime } from "../../../../components/dateTimeUtils.js";

const ViewDrawer = ({ open, onClose, row }) => {
  const { t } = useTranslation();

  const formatDateTime = (dateString) => {
    const formatted = convertUTCToLocalDateTime(dateString, {
      locale: "en-GB",
      formatOptions: {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      },
    });
    return formatted === "—" ? "-" : formatted;
  };

  const InfoBox = ({ children }) => (
    <Box
      mt={2}
      sx={{
        border: "1px solid #091E4224",
        borderRadius: "10px",
        px: 2,
        py: 2,
      }}
    >
      {children}
    </Box>
  );

  const InfoRow = ({ label, value }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2,
      }}
    >
      <CustomText
        sx={{
          ...textStyles.h1,
          fontSize: 16,
          fontWeight: 400,
          color: "#5E5C5C",
        }}
      >
        {label}
      </CustomText>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {value}
      </Box>
    </Box>
  );

  if (!row) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 500, backgroundColor: "white" },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#E7EBEE",
          p: 2,
          py: 1,
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              fontSize: 20,
              fontWeight: 600,
              color: "#2C384C",
              fontFamily: "Poppins, sans-serif",
            }}
          >
          {t("documentlabel")} #{row.id}
          </CustomText>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ px: 2, pb: 2, mt: 2 }}>
        <InfoBox>
          <InfoRow label={t("documentsTable.th.documentName")} value={row.documentName || row.name || "--"} />
          <InfoRow label={t("documentsTable.th.subject")} value={row.subject || "--"} />
          {row.description && (
            <InfoRow 
              label={t("addDocument.form.description") || "Description"} 
              value={
                <CustomText
                  sx={{
                    fontSize: 14,
                    color: "#172B4D",
                    maxWidth: 250,
                    wordBreak: "break-word",
                    textAlign: "right",
                  }}
                >
                  {row.description}
                </CustomText>
              }
            />
          )}
          {/* File Type Row */}
          <InfoRow
            label={t("documentsTable.th.fileType")}
            value={
              (row.fileType || row.file_type || "").toLowerCase().includes("pdf") ? (
                <img src={document1} alt="pdf" style={{ width: 24, height: 24 }} />
              ) : (
                row.fileType || row.file_type || "--"
              )
            }
          />
          {row.access_mode && (
            <InfoRow 
              label={t("documentsTable.th.accessMode")} 
              value={
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: 12,
                    fontWeight: 500,
                    fontFamily: "Poppins, sans-serif",
                    backgroundColor: row.access_mode === "all" ? "#E3F2FD" : "#FFF3E0",
                    color: row.access_mode === "all" ? "#1976D2" : "#E65100",
                    textTransform: "capitalize",
                  }}
                >
                  {row.access_mode}
                </Box>
              }
            />
          )}
          {row.recipient_worker_ids && row.recipient_worker_ids.length > 0 && (
            <InfoRow 
              label={t("documentsTable.th.recipients")}
              value={
                <CustomText
                  sx={{
                    fontSize: 14,
                    color: "#172B4D",
                    maxWidth: 250,
                    wordBreak: "break-word",
                    textAlign: "right",
                  }}
                >
                  {t("documentsTable.filter.recipientsCount", { count: row.recipient_worker_ids.length })}
                </CustomText>
              }
            />
          )}
          {row.file_size && (
            <InfoRow 
              label={t("documentsTable.th.fileSize")}
              value={
                <CustomText sx={{ fontSize: 14, color: "#172B4D" }}>
                  {row.file_size ? `${(row.file_size / (1024 * 1024)).toFixed(2)} MB` : "--"}
                </CustomText>
              }
            />
          )}
          {row.expires_at && (
            <InfoRow 
              label={t("documentsTable.th.expiresAt")} 
              value={formatDateTime(row.expires_at)}
            />
          )}
          <InfoRow label={t("documentsTable.th.dateTime")} value={formatDateTime(row.createdAt || row.created_at)} />
          {row.updated_at && (
            <InfoRow 
              label={t("documentsTable.th.updatedAt")}
              value={formatDateTime(row.updated_at)}
            />
          )}
          {row.owner && (
            <InfoRow 
              label="Owner" 
              value={
                <CustomText sx={{ fontSize: 14, color: "#172B4D" }}>
                  {row.owner.name || row.owner.email || "--"}
                </CustomText>
              }
            />
          )}
        </InfoBox>

            <InfoBox>
  {(row.fileUrl || row.document_url) && ((row.fileUrl || row.document_url || "").toLowerCase().includes("pdf") || (row.fileType || row.file_type || "").toLowerCase().includes("pdf")) && (
    <Box
sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      background: "#f9f9f9",

      borderRadius: "6px",
      p: 2,
      width: "100%",
      height: 250,
      textAlign: "center",
}}
    >
      <img
        src={document1}
        alt="PDF Document"
        style={{ width: 60, height: 60, marginBottom: 8 }}
      />
      <CustomText
        sx={{
          fontSize: 14,
          fontWeight: 400,
          color: "#172B4D",
          textAlign: "center",
          wordBreak: "break-all",
        }}
      >
        {row.documentName || row.name || (row.document_url || row.fileUrl || "").split("/").pop()}
      </CustomText>
    </Box>
  )}
        <Box sx={{ textAlign: "right", mt: 1 }}>
        <CustomButton
          label={t("requestDetails.viewDocument")}
          size="small"
          onClick={() =>
            window.open(row.document_url || row.fileUrl, "_blank", "noopener,noreferrer")
          }
        />
      </Box>
  </InfoBox>
      </Box>
    </Drawer>
  );
};

export default ViewDrawer;